import React, { useEffect } from "react";
import { createPortal } from "react-dom";

// Reusable Bootstrap modal shell.
// - rendered in a portal on <body> so no parent stacking context can hide it
// - explicit z-index (pass a higher `z` for a modal opened over another one)
// - Esc closes only the topmost modal, page scroll is locked while open
export const MODAL_Z = 2000;
const openModals = []; // z-index of every open modal (topmost = max)

export default function ModalShell({
  onClose,
  size = "modal-lg", // modal-sm | modal-lg | modal-xl
  z = MODAL_Z,
  children,
}) {
  useEffect(() => {
    openModals.push(z);
    const onKey = (e) => {
      if (e.key === "Escape" && Math.max(...openModals) === z) onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      openModals.splice(openModals.indexOf(z), 1);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, z]);

  return createPortal(
    <>
      <div
        className="modal fade show d-block"
        style={{ zIndex: z }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className={`modal-dialog ${size} modal-dialog-centered modal-dialog-scrollable`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">{children}</div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: z - 10 }} />
    </>,
    document.body
  );
}

// standard header: title + subtitle + close button
export function ModalHeader({ title, subtitle, onClose }) {
  return (
    <div className="modal-header">
      <div>
        <h5 className="modal-title">{title}</h5>
        {subtitle && <div className="small text-secondary">{subtitle}</div>}
      </div>
      <button
        type="button"
        className="btn-close"
        aria-label="Close"
        onClick={onClose}
      />
    </div>
  );
}
